import { createFileRoute, Link } from "@tanstack/react-router"
import { ArrowLeft } from "lucide-react"
import { useTranslation } from "react-i18next"
import { useAgent } from "@/api/queries/agents"
import { PageContainer } from "@/components/app/page"
import { ErrorState, FormSkeleton } from "@/components/app/states"
import { Button } from "@/components/ui/button"
import { Skeleton } from "@/components/ui/skeleton"
import { AGENT_TABS, AgentEditor, type AgentTab } from "@/features/studio/agent-editor/editor"

export interface AgentEditorSearch {
  tab?: AgentTab
  /** Open the "Try it" playground panel. */
  try?: boolean
}

export const Route = createFileRoute("/_app/studio/agents/$agentId")({
  staticData: { titleKey: "studio.agents.editorTitle" },
  validateSearch: (s: Record<string, unknown>): AgentEditorSearch => ({
    tab: AGENT_TABS.includes(s.tab as AgentTab) && s.tab !== "configure" ? (s.tab as AgentTab) : undefined,
    try: s.try === true || s.try === "true" ? true : undefined,
  }),
  component: AgentEditorPage,
})

function AgentEditorPage() {
  const { t } = useTranslation()
  const { agentId } = Route.useParams()
  const id = Number(agentId)
  const agent = useAgent(id)

  if (agent.isPending)
    return (
      <PageContainer width="full" className="max-w-[110rem]">
        <div className="mb-8 flex items-center gap-3" aria-hidden>
          <Skeleton className="size-11 rounded-lg" />
          <div className="space-y-2">
            <Skeleton className="h-5 w-48" />
            <Skeleton className="h-3.5 w-72" />
          </div>
        </div>
        <Skeleton className="mb-6 h-8 w-80" />
        <div className="max-w-4xl rounded-xl border p-5">
          <FormSkeleton fields={6} />
        </div>
      </PageContainer>
    )

  if (agent.isError)
    return (
      <PageContainer>
        <ErrorState error={agent.error} onRetry={() => void agent.refetch()} className="mt-10" />
        <div className="mt-4 flex justify-center">
          <Button variant="outline" size="sm" nativeButton={false} render={<Link to="/studio/agents" />}>
            <ArrowLeft />
            {t("studio.agents.backToList")}
          </Button>
        </div>
      </PageContainer>
    )

  return <AgentEditor key={agent.data.id} agent={agent.data} />
}
